/**
 * Asset Groups service
 * Manages asset groups with context and category relationships
 */

import { supabase } from '@/lib/supabase'

export interface AssetGroup {
  id: string
  context_id: string
  category_id: string
  key: string
  name: string
  is_system: boolean
  show_in_menu: boolean
  unit_id: string | null
  created_at: string
  // Joined fields
  context_name?: string
  category_name?: string
  // From unit_asset_groups
  is_active?: boolean
  unit_asset_group_id?: string | null
}

export interface AssetGroupInput {
  name: string
  key: string
  context_id: string
  category_id: string
  is_active?: boolean
}

/**
 * Get all asset groups with context and category names
 * Filters: 
 * - If unitId provided: returns system groups (is_system=true) + user groups (unit_id=unitId)
 * - If contextId provided: filters by context
 */
export async function getAllGroups(
  unitId?: string,
  contextId?: string
): Promise<AssetGroup[]> {
  try {
    // Build query with joins
    let query = supabase
      .from('asset_groups')
      .select(`
        *,
        contexts!asset_groups_context_id_fkey (name),
        asset_categories!asset_groups_category_id_fkey (name),
        unit_asset_groups!left (id, active, unit_id)
      `)
      .order('name', { ascending: true })

    // Filter by context if provided
    if (contextId) {
      query = query.eq('context_id', contextId)
    }

    // Filter by unit: system groups OR user's groups
    if (unitId) {
      query = query.or(`is_system.eq.true,unit_id.eq.${unitId}`)
    }

    const { data, error } = await query

    if (error) throw error
    
    // Map the joined data to flat structure and filter unit_asset_groups client-side
    return (data || []).map((item: any) => {
      // Filter unit_asset_groups by unitId if provided
      let unitAssetGroup = null
      if (Array.isArray(item.unit_asset_groups)) {
        unitAssetGroup = unitId 
          ? item.unit_asset_groups.find((uag: any) => uag.unit_id === unitId)
          : item.unit_asset_groups[0]
      } else {
        unitAssetGroup = item.unit_asset_groups
      }

      return {
        ...item,
        context_name: item.contexts?.name,
        category_name: item.asset_categories?.name,
        is_active: unitAssetGroup?.active ?? false,
        unit_asset_group_id: unitAssetGroup?.id ?? null,
        contexts: undefined,
        asset_categories: undefined,
        unit_asset_groups: undefined
      }
    })
  } catch (error) {
    console.error('Error fetching groups:', error)
    throw error
  }
}

/**
 * Get a group by id
 */
export async function getGroupById(id: string): Promise<AssetGroup | null> {
  try {
    const { data, error } = await supabase
      .from('asset_groups')
      .select(`
        *,
        contexts!asset_groups_context_id_fkey (name),
        asset_categories!asset_groups_category_id_fkey (name)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    
    return {
      ...data,
      context_name: data.contexts?.name,
      category_name: data.asset_categories?.name
    }
  } catch (error) {
    console.error('Error fetching group:', error)
    throw error
  }
}

/**
 * Create a new group
 * Automatically creates entry in unit_asset_groups with active=true
 */
export async function createGroup(input: AssetGroupInput, unitId: string): Promise<AssetGroup> {
  try {
    // Insert group
    const { data: groupData, error: groupError } = await supabase
      .from('asset_groups')
      .insert({
        name: input.name,
        key: input.key,
        context_id: input.context_id,
        category_id: input.category_id,
        show_in_menu: true, // Always true for user-created groups
        is_system: false, // User-created groups are never system groups
        unit_id: unitId // Associate with current unit
      })
      .select(`
        *,
        contexts!asset_groups_context_id_fkey (name),
        asset_categories!asset_groups_category_id_fkey (name)
      `)
      .single()

    if (groupError) throw groupError
    
    // Automatically activate this group for the unit
    const { error: activationError } = await supabase
      .from('unit_asset_groups')
      .insert({
        unit_id: unitId,
        group_id: groupData.id,
        active: input.is_active ?? true
      })

    if (activationError) {
      console.error('Error activating group:', activationError)
      // Don't fail the whole operation, just log it
    }
    
    return {
      ...groupData,
      context_name: groupData.contexts?.name,
      category_name: groupData.asset_categories?.name,
      is_active: input.is_active ?? true,
      unit_asset_group_id: null
    }
  } catch (error) {
    console.error('Error creating group:', error)
    throw error
  }
}

/**
 * Update a group (only for non-system groups)
 * Optionally updates the active status in unit_asset_groups if unitId is provided
 */
export async function updateGroup(id: string, input: AssetGroupInput, unitId?: string): Promise<AssetGroup> {
  try {
    const { data, error } = await supabase
      .from('asset_groups')
      .update({
        name: input.name,
        // Key cannot be updated for system groups
        context_id: input.context_id,
        category_id: input.category_id
      })
      .eq('id', id)
      .eq('is_system', false) // Only allow updates on non-system groups
      .select(`
        *,
        contexts!asset_groups_context_id_fkey (name),
        asset_categories!asset_groups_category_id_fkey (name)
      `)
      .single()

    if (error) throw error
    
    // Update unit_asset_groups active status if provided
    if (unitId && input.is_active !== undefined) {
      const { error: activationError } = await supabase
        .from('unit_asset_groups')
        .update({ active: input.is_active })
        .eq('unit_id', unitId)
        .eq('group_id', id)

      if (activationError) {
        console.error('Error updating group activation:', activationError)
        // Don't fail the whole operation, just log it
      }
    }
    
    return {
      ...data,
      context_name: data.contexts?.name,
      category_name: data.asset_categories?.name,
      is_active: input.is_active
    }
  } catch (error) {
    console.error('Error updating group:', error)
    throw error
  }
}

/** (only non-system groups)
 */
export async function deleteGroup(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('asset_groups')
      .delete()
      .eq('id', id)
      .eq('is_system', false) // Only allow deletion of non-system groups

    if (error) throw error
  } catch (error) {
    console.error('Error deleting group:', error)
    throw error
  }
}

/**
 * Toggle group activation for a unit
 * Updates the unit_asset_groups table
 */
export async function toggleGroupActivation(
  groupId: string, 
  unitId: string, 
  active: boolean
): Promise<void> {
  try {
    // Check if record exists
    const { data: existing } = await supabase
      .from('unit_asset_groups')
      .select('id')
      .eq('group_id', groupId)
      .eq('unit_id', unitId)
      .single()

    if (existing) {
      // Update existing record
      const { error } = await supabase
        .from('unit_asset_groups')
        .update({ active, updated_at: new Date().toISOString() })
        .eq('id', existing.id)

      if (error) throw error
    } else {
      // Insert new record
      const { error } = await supabase
        .from('unit_asset_groups')
        .insert({
          unit_id: unitId,
          group_id: groupId,
          active
        })

      if (error) throw error
    }
  } catch (error) {
    console.error('Error toggling group activation:', error)
    throw error
  }
}

/**
 * Check if a key is already in use (for validation)
 */
export async function isKeyAvailable(key: string, excludeId?: string): Promise<boolean> {
  try {
    let query = supabase
      .from('asset_groups')
      .select('id')
      .eq('key', key)

    if (excludeId) {
      query = query.neq('id', excludeId)
    }

    const { data, error } = await query

    if (error) throw error
    return !data || data.length === 0
  } catch (error) {
    console.error('Error checking key availability:', error)
    return false
  }
}
